// imports from npm
import React, { Component } from "react";
import { withStyles, WithStyles, createStyles, Theme, Table, TableBody, TableRow, TableCell } from "@material-ui/core";
import { Card, CardActions, CardContent, TextField } from "@material-ui/core";
// local imports
import ActionButtons from "src/internal/components/Common/ActionButtons";
import { AppointmentSurveyDto } from "src/external/types/appointmentSurvey";
import { QuestionResponseDto } from "src/external/types/questionResponse";
import { cssVars, rootFontSize } from "src/globalCssVars";
import Rating from "./Rating";
import FeedbackRequestDialog from "../Appointments/FeedbackRequestDialog";

interface FeedbackCollectionFormOwnProps {
  survey: AppointmentSurveyDto;
  isMobile?: boolean;
  onSave: (appointmentSurvey: AppointmentSurveyDto) => void;
}

interface FeedbackCollectionFormState {
  apptSurvey: AppointmentSurveyDto;
  initializedApptSurvey: AppointmentSurveyDto;
  isDirty: boolean;
  isSaving: boolean;
  feedbackRequestDialogOpen: boolean;
}

interface FeedbackCollectionFormProps extends FeedbackCollectionFormOwnProps, WithStyles<typeof styles> {}

class FeedbackCollectionForm extends Component<FeedbackCollectionFormProps, FeedbackCollectionFormState> {
  constructor(props: FeedbackCollectionFormProps) {
    super(props);
    let surveyCopy = JSON.parse(JSON.stringify(props.survey));
    this.state = {
      apptSurvey: {
        ...props.survey
      },
      initializedApptSurvey: {
        ...surveyCopy
      },
      isDirty: false,
      isSaving: false,
      feedbackRequestDialogOpen: false
    };
  }

  updateResponseRatingState = async (question: QuestionResponseDto, rating: number) => {
    let copy = this.state.apptSurvey.responses.map(r => {
      return { ...r };
    });
    let found = copy.find(r => r.surveyQuestion.displayOrder == question.surveyQuestion.displayOrder);
    if (found) {
      found.rating = rating;
    }
    this.setState({
      apptSurvey: {
        ...this.state.apptSurvey,
        responses: copy
      },
      isDirty: true
    });
  };

  handleFreeTextInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      apptSurvey: {
        ...this.state.apptSurvey,
        freeTextResponse: event.currentTarget.value
      },
      isDirty: true
    });
  };

  handleSave = async () => {
    var feedbackWasAboveThreshold = await this.validateFeedbackRequestForLowScore();
    if (feedbackWasAboveThreshold == true) {
      this.completeSave();
    }
  };

  completeSave = () => {
    this.setState({
      isSaving: true
    });
    this.props.onSave(this.state.apptSurvey);
    //onSave redirects page, so no need to set state here
  };

  validateFeedbackRequestForLowScore = async () => {
    if (this.state.apptSurvey.freeTextResponse == "") {
      var thresholdScore = this.state.apptSurvey.survey.threshold;
      if (this.state.apptSurvey.responses) {
        var responseValue = 0;
        this.state.apptSurvey.responses.forEach(response => {
          responseValue += response.rating;
        });
        responseValue = responseValue / this.state.apptSurvey.responses.length;
        if (responseValue < thresholdScore) {
          this.setState({
            feedbackRequestDialogOpen: true
          });
          return false;
        }
      }
    }
    return true;
  };

  addRequestedFeedbackToSurvey = (feedback: string) => {
    this.setState(
      {
        apptSurvey: {
          ...this.state.apptSurvey,
          freeTextResponse: feedback
        },
        feedbackRequestDialogOpen: false
      },
      this.completeSave
    );
  };

  cancelFeedbackRequest = () => {
    this.setState(
      {
        feedbackRequestDialogOpen: false
      },
      this.completeSave
    );
  };

  handleCancel = async () => {
    this.state.initializedApptSurvey.responses.map(r => {
      this.updateResponseRatingState(r, r.rating);
    });
    this.setState({
      apptSurvey: this.state.initializedApptSurvey,
      isDirty: false
    });
  };

  render() {
    const { classes, isMobile } = this.props;
    const { apptSurvey, isDirty, isSaving, feedbackRequestDialogOpen } = this.state;

    return (
      <Card className={isMobile ? classes.MobileCard : classes.WebCard}>
        {feedbackRequestDialogOpen && (
          <FeedbackRequestDialog
            isMobile={isMobile ? isMobile : false}
            isOpen={this.state.feedbackRequestDialogOpen}
            onCancelFeedbackRequest={this.cancelFeedbackRequest}
            onConfirmFeedbackRequest={this.addRequestedFeedbackToSurvey}
          />
        )}
        <CardContent>
          <Table className={classes.Table}>
            <TableBody>
              <TableRow>
                <TableCell>
                  <b>Location:</b>
                  <br /> {apptSurvey.appointment.locationName}
                </TableCell>
                <TableCell align="right">
                  <b>Agent:</b>
                  <br /> {apptSurvey.appointment.serviceRepName}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <b>Purpose:</b>
                  <br /> {apptSurvey.appointment.reasonName}
                </TableCell>
                <TableCell align="right">
                  <b>Date/Time:</b>
                  <br />{" "}
                  {`${new Intl.DateTimeFormat("en-US", {
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric"
                  }).format(new Date(apptSurvey.appointment.appointmentTime))} ${apptSurvey.appointment.timeZone.abbr}`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {apptSurvey.responses.map((questionToRespondTo, i) => (
            <div key={i}>
              <span className={classes.Question}>{questionToRespondTo.surveyQuestion.question}</span>
              <Rating response={questionToRespondTo} handleChangeRating={this.updateResponseRatingState} />
            </div>
          ))}
          <TextField
            margin="normal"
            variant="outlined"
            type="text"
            multiline={true}
            rows={4}
            inputProps={{ maxLength: 1000 }}
            label="Enter any additional feedback"
            value={apptSurvey.freeTextResponse}
            fullWidth={true}
            onChange={this.handleFreeTextInputChange}
          />
        </CardContent>
        <CardActions>
          <ActionButtons
            className={classes.ActionButtons}
            saveButtonText="Submit"
            onSubmit={this.handleSave}
            cancelButtonText="Clear"
            onCancel={this.handleCancel}
            disabled={!isDirty || isSaving}
            isSubmitting={isSaving}
          />
        </CardActions>
      </Card>
    );
  }
}

const styles = (theme: Theme) => {
  const listLeftOffset = // Left-align the list with the top header.
    cssVars.webStepperMinWidth + // Stepper width
    2 * theme.spacing.unit + // plus the left and right margin on the stepper
    theme.spacing.unit; // plus the left margin of the content container
  return createStyles({
    ActionButtons: {
      display: "flex",
      justifyContent: "flex-end",
      width: "100%"
    },
    Table: {
      marginBottom: theme.spacing.unit * 2 + "rem"
    },
    Question: {
      fontFamily: "Roboto, sans-serif",
      textAlign: "center",
      width: "100%",
      wordWrap: "break-word",
      display: "block"
    },
    MobileCard: {
      width: "100%"
    },
    WebCard: {
      height: "100%",
      maxWidth: `${700 / rootFontSize}rem`,
      marginLeft: `${listLeftOffset}rem`
    }
  });
};

export default withStyles(styles)(FeedbackCollectionForm);
